import React from 'react';
import { Input, Space, Button, Dropdown, Spin, message, Modal, Select } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { SearchOutlined, EllipsisOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import CommonTable from 'components/CommonTable';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import type { EFaxListRow, SentFaxStatus, FaxContact } from 'types/eFax';
import { FAX_TAG_OPTIONS } from 'types/eFax';
import { faxSentList, faxDeleteMessage, faxDownload, faxCancelMessage, faxResend, faxAssignPatient } from 'api/fax';
import { getProspectList } from 'api/prospect';
import type { Prospect } from 'types/common';
import {
    extractFaxPagedResponse,
    findFaxContactByNumber,
    mapOutboundFaxVo,
    isFaxScheduleSuccess,
    formatFaxNumber,
} from 'utils/eFaxScheduleRows';
import FaxDetailDrawer from '../FaxDetailDrawer';
import s from '../s.module.less';
import { formatUTCTime } from 'utils/common';
import { useRequest } from 'ahooks';
import AiTechnologySparkIcon from 'assets/figma/efax/ai-technology-spark.svg';

interface Props {
    contacts: FaxContact[];
    onAddContact?: (faxNumber: string) => void;
    refreshKey?: number;
}

const STATUS_BADGE_MAP: Record<SentFaxStatus, string> = {
    processing: s.badgeProcessing,
    delivered: s.badgeDelivered,
    cancelled: s.badgeCancelled,
    failed: s.badgeFailed,
    chargeback: s.badgeChargeback,
    refunded: s.badgeRefunded,
    paid: s.badgePaid,
};

const STATUS_LABEL_MAP: Record<SentFaxStatus, string> = {
    processing: 'Processing',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    failed: 'Failed',
    chargeback: 'Chargeback',
    refunded: 'Refunded',
    paid: 'Paid',
};

const STATUS_QUESTION_ICON: Partial<Record<SentFaxStatus, boolean>> = {
    chargeback: true,
    refunded: true,
    paid: true,
};

const DEFAULT_PAGE_SIZE = 20;

const toOrderType = (order: 'ascend' | 'descend' | undefined) => {
    if (order === 'ascend') return 'ASC' as const;
    if (order === 'descend') return 'DESC' as const;
    return undefined;
};

const toSortOrder = (order: SorterResult<unknown>['order']) => {
    if (order === 'ascend') return 'ascend' as const;
    if (order === 'descend') return 'descend' as const;
    return undefined;
};

const SentFaxTab: React.FC<Props> = ({ contacts, onAddContact, refreshKey }) => {
    const navigate = useNavigate();
    const [data, setData] = React.useState<EFaxListRow[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [total, setTotal] = React.useState(0);
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
    const [detailUuid, setDetailUuid] = React.useState<string | null>(null);
    const [detailOpen, setDetailOpen] = React.useState(false);

    const [sendTo, setSendTo] = React.useState('');
    const [subject, setSubject] = React.useState('');
    const [patientName, setPatientName] = React.useState('');
    const [sendToDraft, setSendToDraft] = React.useState('');
    const [subjectDraft, setSubjectDraft] = React.useState('');
    const [patientDraft, setPatientDraft] = React.useState('');

    const [tagFilters, setTagFilters] = React.useState<string[]>([]);
    const [statusFilters, setStatusFilters] = React.useState<string[]>([]);
    const [sentOnOrder, setSentOnOrder] = React.useState<'descend' | 'ascend' | undefined>(undefined);

    // Assign patient modal (same logic as Received fax tab)
    const [assignTargetId, setAssignTargetId] = React.useState<string | null>(null);
    const [showAssignModal, setShowAssignModal] = React.useState(false);
    const [selectedPatientId, setSelectedPatientId] = React.useState<number | undefined>(undefined);
    const [assignLoading, setAssignLoading] = React.useState(false);

    const { data: prospectData, run: fetchProspects } = useRequest(getProspectList, { manual: true });
    const prospectList: Prospect[] = (prospectData?.data?.data as Prospect[] | undefined) || [];
    const clientOptions = prospectList.filter((p) => !p.isSample);

    const fetchList = React.useCallback(async () => {
        setLoading(true);
        const result = await faxSentList({
            current: page,
            size: pageSize,
            sortBy: 'SEND_ON',
            orderType: toOrderType(sentOnOrder),
            sendTo: sendTo.trim() || undefined,
            subject: subject.trim() || undefined,
            patientName: patientName.trim() || undefined,
            tags: tagFilters.length ? tagFilters : undefined,
            status: statusFilters.length ? statusFilters : undefined,
        });
        const body = result?.data;
        if (!result.error && body) {
            const { list, totalRecords } = extractFaxPagedResponse(body);
            setData(list.map(mapOutboundFaxVo));
            setTotal(totalRecords);
        } else {
            setData([]);
            setTotal(0);
        }
        setLoading(false);
    }, [page, pageSize, sendTo, subject, patientName, tagFilters, statusFilters, sentOnOrder]);

    React.useEffect(() => {
        fetchList();
    }, [fetchList]);

    React.useEffect(() => {
        if (refreshKey !== undefined && refreshKey > 0) {
            fetchList();
        }
    }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const makeSearchFilterDropdown = (
        draft: string,
        setDraft: (v: string) => void,
        applyCommitted: (value: string) => void,
        fieldLabel: string,
    ) => {
        return () => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={`Search ${fieldLabel}...`}
                    style={{ marginBottom: 8, display: 'block' }}
                    onKeyDown={(e) => e.key === 'Enter' && e.stopPropagation()}
                />
                <Space>
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                        onClick={() => {
                            applyCommitted(draft);
                            setPage(1);
                        }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => {
                            setDraft('');
                            applyCommitted('');
                            setPage(1);
                        }}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        );
    };

    const handleTableChange: TableProps<EFaxListRow>['onChange'] = (pagination, filters, sorter) => {
        const newPs = pagination?.pageSize;
        const newCur = pagination?.current;
        if (newPs !== undefined && newPs !== pageSize) {
            setPageSize(newPs);
            setPage(newCur ?? 1);
        } else if (newCur !== undefined) {
            setPage(newCur);
        }

        const nextTags = (filters?.tag as FilterValue)?.filter((x): x is string => typeof x === 'string') ?? [];
        const nextStatus =
            (filters?.status as FilterValue)?.filter((x): x is string => typeof x === 'string') ?? [];
        const tagsChanged = JSON.stringify(nextTags) !== JSON.stringify(tagFilters);
        const statusChanged = JSON.stringify(nextStatus) !== JSON.stringify(statusFilters);
        setTagFilters(nextTags);
        setStatusFilters(nextStatus);
        if (tagsChanged || statusChanged) setPage(1);

        const sr = (Array.isArray(sorter) ? sorter[0] : sorter) as SorterResult<EFaxListRow> | undefined;
        if (sr?.columnKey === 'sentOn') {
            const nextOrder = toSortOrder(sr.order);
            if (nextOrder !== sentOnOrder) {
                setSentOnOrder(nextOrder);
                setPage(1);
            }
        }
    };

    const handleDownload = async (record: EFaxListRow) => {
        const result = await faxDownload(record.id);
        const body = result?.data as { code?: number; data?: string } | undefined;
        if (!result?.error && body && isFaxScheduleSuccess(body) && body.data) {
            window.open(body.data, '_blank');
        } else {
            message.error('Download failed');
        }
    };

    const handleCancel = (record: EFaxListRow) => {
        Modal.confirm({
            title: 'Cancel fax',
            content: 'Are you sure you want to cancel this fax?',
            okText: 'Cancel fax',
            okButtonProps: { danger: true },
            onOk: async () => {
                const result = await faxCancelMessage(record.id);
                const body = result?.data as { code?: number; message?: string } | undefined;
                const ok = !result?.error && (!body || isFaxScheduleSuccess(body));
                if (!ok) {
                    message.error(body?.message || 'Failed to cancel fax');
                    return;
                }
                message.success('Fax cancelled');
                fetchList();
            },
        });
    };

    const handleResend = async (record: EFaxListRow) => {
        const result = await faxResend(record.id);
        const body = result?.data as { code?: number; message?: string } | undefined;
        const ok = !result?.error && (!body || isFaxScheduleSuccess(body));
        if (!ok) {
            message.error(body?.message || 'Failed to retry fax');
            return;
        }
        message.success('Fax retry started');
        fetchList();
    };

    const handleDelete = (record: EFaxListRow) => {
        Modal.confirm({
            title: 'Delete fax',
            content: 'Are you sure you want to delete this fax?',
            okText: 'Delete',
            okButtonProps: { danger: true },
            onOk: async () => {
                const result = await faxDeleteMessage(record.id);
                const body = result?.data as { code?: number; message?: string } | undefined;
                const ok = !result?.error && (!body || isFaxScheduleSuccess(body));
                if (!ok) {
                    message.error(body?.message || 'Failed to delete');
                    return;
                }
                message.success('Fax deleted');
                fetchList();
            },
        });
    };

    const handleView = (record: EFaxListRow) => {
        setDetailUuid(record.id);
        setDetailOpen(true);
    };

    const handleOpenAssign = (record: EFaxListRow) => {
        fetchProspects();
        setAssignTargetId(record.id);
        setSelectedPatientId(undefined);
        setShowAssignModal(true);
    };

    const handleConfirmAssign = async () => {
        if (!assignTargetId || !selectedPatientId) return;
        setAssignLoading(true);
        try {
            const result = await faxAssignPatient(assignTargetId, selectedPatientId);
            const body = result?.data as { code?: number; message?: string } | undefined;
            if (result?.error || !body || !isFaxScheduleSuccess(body)) {
                message.error(body?.message || 'Failed to assign patient');
                return;
            }
            message.success('Patient assigned');
            setShowAssignModal(false);
            fetchList();
        } finally {
            setAssignLoading(false);
        }
    };

    const columns: ColumnsType<EFaxListRow> = [
        {
            title: 'SENT TO',
            dataIndex: 'faxNumber',
            width: 200,
            filterIcon: <SearchOutlined />,
            filterDropdown: makeSearchFilterDropdown(sendToDraft, setSendToDraft, setSendTo, 'recipient'),
            filteredValue: sendTo ? [sendTo] : null,
            render: (_, record) => {
                const faxNum = record.faxNumber;
                const matched = findFaxContactByNumber(contacts, record.faxNumber);
                let name = matched?.name?.trim() || record.faxCsid;
                if (record.users && record.users.length > 0) {
                    name = `${record.users[0].firstName} ${record.users[0].lastName}`;
                }
                const inContacts = Boolean(matched);
                const faxNumDisplay = formatFaxNumber(faxNum) || faxNum;
                const contactDisplayName = (name || '').trim() || undefined;
                return (
                    <div className={s.sentFromCell}>
                        <span className={inContacts ? s.readDot : s.unreadDot} />
                        <div className={s.sentFromInfo}>
                            {contactDisplayName ? (
                                <>
                                    <span className={s.sentFromContact}>{contactDisplayName}</span>
                                    <span className={s.sentFromSecondaryRow}>
                                        <span className={s.sentFromSecondaryText}>{faxNumDisplay}</span>
                                        {!inContacts && (
                                            <button
                                                type="button"
                                                aria-label="Add to contact"
                                                title="Add to contact"
                                                onClick={() => onAddContact?.(record.faxNumber)}
                                                className={s.addContactCircleBtn}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M12 9V12M12 12V15M12 12H15M12 12H9M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                                        stroke="#111827"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                    </span>
                                </>
                            ) : (
                                <span className={s.sentFromPrimaryRow}>
                                    <span className={s.sentFromNumber}>{faxNumDisplay}</span>
                                    {!inContacts && (
                                        <button
                                            type="button"
                                            aria-label="Add to contact"
                                            title="Add to contact"
                                            onClick={() => onAddContact?.(record.faxNumber)}
                                            className={s.addContactCircleBtn}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                            >
                                                <path
                                                    d="M12 9V12M12 12V15M12 12H15M12 12H9M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                                    stroke="#111827"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </button>
                                    )}
                                </span>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'SUBJECT',
            dataIndex: 'subject',
            width: 200,
            filterIcon: <SearchOutlined />,
            filterDropdown: makeSearchFilterDropdown(subjectDraft, setSubjectDraft, setSubject, 'subject'),
            filteredValue: subject ? [subject] : null,
            render: (text: string) => text || '-',
        },
        {
            title: 'STATUS',
            dataIndex: 'status',
            width: 130,
            filteredValue: statusFilters.length ? statusFilters : null,
            filters: (['processing', 'delivered', 'cancelled', 'failed'] as SentFaxStatus[]).map((st) => ({
                text: STATUS_LABEL_MAP[st],
                value: st,
            })),
            render: (status: string) => {
                const st = status as SentFaxStatus;
                return (
                    <span className={STATUS_BADGE_MAP[st] || s.badgeProcessing}>
                        {STATUS_LABEL_MAP[st] || status}
                        {STATUS_QUESTION_ICON[st] && (
                            <QuestionCircleOutlined style={{ marginLeft: 4, fontSize: 12 }} />
                        )}
                    </span>
                );
            },
        },
        {
            title: 'TAG',
            dataIndex: 'tag',
            width: 160,
            filteredValue: tagFilters.length ? tagFilters : null,
            filters: FAX_TAG_OPTIONS.map((t) => ({ text: t, value: t })),
            filterMultiple: true,
            render: (tag: string) => (tag ? <span className={s.badgeTag}>{tag}</span> : '-'),
        },
        {
            title: 'PATIENT',
            dataIndex: 'patient',
            width: 150,
            filterIcon: <SearchOutlined />,
            filterDropdown: makeSearchFilterDropdown(
                patientDraft,
                setPatientDraft,
                setPatientName,
                'patient',
            ),
            filteredValue: patientName ? [patientName] : null,
            render: (text: string, record: EFaxListRow) => {
                const patientNameText = (text || '').trim();
                const patientId = record.patientId;
                const display = patientNameText || (patientId ? `ID: ${patientId}` : '');
                if (!display) return '-';

                const canNavigate = typeof patientId === 'number' && !Number.isNaN(patientId);
                return (
                    <div className={s.patientCell}>
                        {record.aiMatched && (
                            <span className={s.aiMatchedLabel}>
                                <img
                                    className={s.aiMatchedIcon}
                                    src={AiTechnologySparkIcon}
                                    alt=""
                                    aria-hidden="true"
                                />
                                <span>AI-matched</span>
                            </span>
                        )}
                        <button
                            type="button"
                            className={record.aiMatched ? s.patientLinkBtnAi : s.patientLinkBtnManual}
                            disabled={!canNavigate}
                            onClick={() => {
                                if (!canNavigate) return;
                                navigate(`${PATH.DASHBOARD}/${PATH.PROSPECTS_DETAIL}/${patientId}`);
                            }}
                        >
                            {display}
                        </button>
                    </div>
                );
            },
        },
        {
            title: 'SENT ON',
            key: 'sentOn',
            dataIndex: 'createdAt',
            width: 160,
            sorter: true,
            sortOrder: sentOnOrder,
            render: (date: string) => (date ? formatUTCTime(date, 'MM/DD/YY') : '-'),
        },
        {
            title: '',
            key: 'viewActions',
            width: 100,
            render: (_, record) => {
                const inContacts = Boolean(findFaxContactByNumber(contacts, record.faxNumber));
                return (
                    <div className={s.viewActionsCell}>
                        <button type="button" className={s.viewLinkBtn} onClick={() => handleView(record)}>
                            View
                        </button>
                        <Dropdown
                            menu={{
                                items: [
                                    ...(inContacts ? [] : [{ key: 'addContact', label: 'Add to contact' }]),
                                    ...(record.patient
                                        ? [{ key: 'reassignPatient', label: 'Reassign patient' }]
                                        : [{ key: 'assignPatient', label: 'Assign patient' }]
                                    ),
                                    ...(record.status === 'failed' ? [{ key: 'retry', label: 'Retry' }] : []),
                                    { key: 'download', label: 'Download' },
                                    ...(record.status === 'processing' ? [{ key: 'cancel', label: 'Cancel', danger: true }] : []),
                                    { key: 'delete', label: 'Delete', danger: true },
                                ],
                                onClick: ({ key }) => {
                                    if (key === 'addContact') onAddContact?.(record.faxNumber);
                                    if (key === 'reassignPatient') handleOpenAssign(record);
                                    if (key === 'assignPatient') handleOpenAssign(record);
                                    if (key === 'retry') handleResend(record);
                                    if (key === 'download') handleDownload(record);
                                    if (key === 'cancel') handleCancel(record);
                                    if (key === 'delete') handleDelete(record);
                                },
                            }}
                            trigger={['click']}
                        >
                            <button type="button" className={s.iconBtn} aria-label="More actions">
                                <EllipsisOutlined className={s.ellipsisIcon} />
                            </button>
                        </Dropdown>
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <Spin spinning={loading}>
                <CommonTable<EFaxListRow>
                    data={data}
                    columns={columns}
                    rowKey="id"
                    pagination={{
                        current: page,
                        pageSize,
                        total,
                        showSizeChanger: false,
                    }}
                    onChange={handleTableChange}
                    locale={{
                        emptyText: <div className={s.emptyText}>No faxes sent yet</div>,
                    }}
                />
            </Spin>

            <FaxDetailDrawer
                open={detailOpen}
                uuid={detailUuid}
                onClose={() => setDetailOpen(false)}
                onUpdated={() => fetchList()}
            />

            <Modal
                title="Assign patient"
                open={showAssignModal}
                onCancel={() => setShowAssignModal(false)}
                onOk={handleConfirmAssign}
                okText="Assign"
                confirmLoading={assignLoading}
                okButtonProps={{ disabled: !selectedPatientId }}
                destroyOnClose
            >
                <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder="Search patient..."
                    value={selectedPatientId}
                    onChange={(val) => setSelectedPatientId(val)}
                    filterOption={(input, option) =>
                        ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={clientOptions.map((p) => ({
                        value: p.id,
                        label: `${p.firstName} ${p.lastName}`,
                    }))}
                />
            </Modal>
        </>
    );
};

export default SentFaxTab;
